import { Coins, Clock, Package, AlertTriangle } from "lucide-react";
import { formatPrice } from "./BazaarListingCard";

function StatCard({ icon: Icon, label, value, testId, warn }) {
  return (
    <div className={`rounded-lg border p-3 space-y-1 ${warn ? "border-[#D90429]/40 bg-[#D90429]/5" : "border-white/10 bg-black/20"}`} data-testid={testId}>
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f1e0c5]/40"><Icon className="w-3 h-3" /> {label}</span>
      <p className={`font-display text-xl ${warn ? "text-[#D90429]" : "text-[#D4AF37]"}`}>{value}</p>
    </div>
  );
}

// Merchant Dashboard — shown on a merchant's own Knights Fee profile so they can see how the
// Bazaar is doing for them without leaving the social side of the app. Pulls from
// GET /api/bazaar/merchant/dashboard (see bazaar_router.py) — plain stat cards + a recent-sales
// list on purpose, no chart lib, so nobody has to guess what a number means or whether a payout
// actually landed.
export function MerchantDashboardWidget({ dashboard }) {
  if (!dashboard) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-5 space-y-4" data-testid="merchant-dashboard-widget">
      <h3 className="font-display text-lg flex items-center gap-2"><Coins className="w-4 h-4 text-[#D4AF37]" /> Merchant Dashboard</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Coins} label="Lifetime Earned" value={formatPrice(dashboard.lifetime_earned_cents)} testId="merchant-stat-earned" />
        <StatCard icon={Clock} label="Pending Payout" value={formatPrice(dashboard.pending_payout_cents)} testId="merchant-stat-pending" warn={dashboard.needs_attention} />
        <StatCard icon={Package} label="Active Listings" value={dashboard.active_listings_count} testId="merchant-stat-listings" />
        <StatCard icon={Coins} label="Total Sales" value={dashboard.total_sales_count} testId="merchant-stat-sales" />
      </div>
      {dashboard.needs_attention && (
        <p className="flex items-center gap-1.5 text-xs text-[#D90429]" data-testid="merchant-needs-attention-notice">
          <AlertTriangle className="w-3.5 h-3.5" /> One or more payouts didn't go through — check your Stripe account is fully onboarded.
        </p>
      )}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/40">Recent Sales</p>
        {dashboard.recent_sales.length === 0 ? (
          <p className="text-xs text-[#f1e0c5]/30" data-testid="merchant-no-sales">No sales yet — list something in the Bazaar to start earning.</p>
        ) : (
          dashboard.recent_sales.map((s, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`merchant-recent-sale-${i}`}>
              <div>
                <p className="text-sm">{s.title}</p>
                <p className="text-[10px] text-[#f1e0c5]/40 uppercase tracking-wide">
                  {s.payout_status === "transferred" ? "Paid out" : s.payout_status === "transfer_failed" ? "Payout failed" : s.payout_status === "skipped_no_account" ? "No payout account" : "Pending payout"}
                </p>
              </div>
              <span className="text-[#D4AF37] font-display">{formatPrice(s.seller_cut_cents)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
